import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from '../../action/logout-action';
import { ProjectType, parseProjectType } from '../../../virtco/components/project/createProject/enums';
import {
  clearSharedProjectDetailsCache,
  getSharedProjectDetailsResponse,
} from './shared-project-details-fetch';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  url: string;
  type: string;
}

interface FirstStepState {
  id: string | null;
  title: string;
  description: string;
  projectType: string; // "fixed" or "hourly"
  uploadedFiles: UploadedFile[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  fetchingId: string | null;
}

const initialState: FirstStepState = {
  id: null,
  title: '',
  description: '',
  projectType: 'fixed',
  uploadedFiles: [],
  status: 'idle',
  fetchingId: null,
};

export const fetchProjectFirstStep = createAsyncThunk(
  'firstStep/fetchProjectFirstStep',
  async (projectId: string) => {
    const responseData = await getSharedProjectDetailsResponse(projectId);
    const projectDto = responseData?.projectDto || responseData?.project || responseData;
    
    let uploadedFiles: UploadedFile[] = [];
    const mediaFiles = responseData?.projectMediaFiles || responseData?.projectImagesUrls || [];
    
    if (Array.isArray(mediaFiles) && mediaFiles.length > 0) {
      uploadedFiles = mediaFiles
        .filter((img: any) => !img.isDeleted)
        .map((img: any) => {
          const fileUrl = img.filePath || img.imageUrl || img.url || img.fileUrl || '';
          return {
            id: img.id || img.fileId || '',
            name: img.fileName || img.name || 'image',
            size: img.fileSize || img.size || 0,
            url: fileUrl,
            type: img.mimeType || img.type || 'image/jpeg'
          };
        });
    }
    
    // Convert API enum value (number or legacy string) to UI string format
    const getProjectTypeString = (projectTypeEnum: ProjectType | string | number | null | undefined): string => {
      const parsed = parseProjectType(projectTypeEnum);
      if (parsed === ProjectType.HourlyRate) return 'hourly';
      return 'fixed';
    };

    const apiProjectType = projectDto?.projectType || projectDto?.type;
    const projectTypeString = getProjectTypeString(apiProjectType);
    
    return {
      id: projectDto?.id || null,
      title: projectDto?.title || '',
      description: projectDto?.description || '',
      projectType: projectTypeString,
      uploadedFiles,
    };
  }
);

const firstStepSlice = createSlice({
  name: 'firstStep',
  initialState,
  reducers: {
    setFirstStepData: (state, action: PayloadAction<Partial<FirstStepState>>) => {
      return { ...state, ...action.payload };
    },
    setData(state, action: PayloadAction<{ id?: string; title: string; description: string; projectType: string; uploadedFiles: UploadedFile[] }>) {
      state.id = action.payload.id || null;
      state.title = action.payload.title;
      state.description = action.payload.description;
      state.projectType = action.payload.projectType;
      state.uploadedFiles = action.payload.uploadedFiles;
    },
    clearData(state) {
      state.id = null;
      state.title = '';
      state.description = '';
      state.projectType = 'fixed';
      state.uploadedFiles = [];
      state.status = 'idle';
      // Block in-flight responses until a new pending sets the real project id
      state.fetchingId = '';
      clearSharedProjectDetailsCache();
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchProjectFirstStep.pending, (state, action) => {
        state.status = 'loading';
        state.fetchingId = action.meta.arg;
      })
      .addCase(fetchProjectFirstStep.fulfilled, (state, action) => {
        // '' = clearData (block late responses); other id = newer request owns the slot
        if (state.fetchingId === '' || (state.fetchingId != null && state.fetchingId !== action.meta.arg)) {
          return;
        }
        state.status = 'succeeded';
        state.id = action.payload.id;
        state.title = action.payload.title || '';
        state.description = action.payload.description || '';
        state.projectType = action.payload.projectType || 'fixed';
        state.uploadedFiles = action.payload.uploadedFiles || [];
        state.fetchingId = null;
      })
      .addCase(fetchProjectFirstStep.rejected, (state, action) => {
        if (state.fetchingId !== action.meta.arg) {
          return;
        }
        state.status = 'failed';
        state.fetchingId = null;
      })
      .addCase(logout, () => initialState);
  },
});

export const { setFirstStepData, setData, clearData } = firstStepSlice.actions;
export default firstStepSlice.reducer;

// Selectors
export const selectProjectId = (state: any) => state.firstStep.id;
export const selectFirstStepStatus = (state: any) => state.firstStep.status;

